import { useEffect } from "react";
import { SITE_URL } from "@/constants";

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface PageMetaProps {
  type: "website" | "article";
  headline: string;
  description: string;
  datePublished?: string;
  dateModified?: string;
  articleSection?: string;
  faqs?: FAQItem[];
  breadcrumbItems?: BreadcrumbItem[];
}

export default function PageMeta({
  type,
  headline,
  description,
  datePublished,
  dateModified,
  articleSection,
  faqs,
  breadcrumbItems,
}: PageMetaProps) {
  const siteUrl: string = SITE_URL;

  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];

    const injectJSONLD = (data: Record<string, unknown>) => {
      const existing = document.querySelector('script[data-seo="true"]');
      if (existing) existing.remove();

      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "true");
      script.textContent = JSON.stringify(data, null, 0);
      document.head.appendChild(script);
      scripts.push(script);
    };

    const baseSchema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": type === "article" ? "Article" : "WebSite",
    };

    if (type === "website") {
      injectJSONLD({
        ...baseSchema,
        name: headline,
        description,
        url: siteUrl,
        potentialAction: {
          "@type": "SearchAction",
          target: `${siteUrl}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      });

      const orgScript = document.createElement("script");
      orgScript.type = "application/ld+json";
      orgScript.setAttribute("data-seo-org", "true");
      const existingOrg = document.querySelector('script[data-seo-org="true"]');
      if (existingOrg) existingOrg.remove();
      orgScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Superbuy Spreadsheet",
        url: siteUrl,
        description: "The most trusted superbuy spreadsheet link directory for verified shopping links.",
        sameAs: [
          "https://kaspreadsheet.com/",
        ],
      }, null, 0);
      document.head.appendChild(orgScript);
      scripts.push(orgScript);
    }

    if (type === "article") {
      const articleJSON: Record<string, unknown> = {
        ...baseSchema,
        headline,
        description,
        url: `${siteUrl}${window.location.pathname}`,
        author: {
          "@type": "Organization",
          name: "Superbuy Spreadsheet",
          url: siteUrl,
        },
        publisher: {
          "@type": "Organization",
          name: "Superbuy Spreadsheet",
          url: siteUrl,
        },
      };

      if (datePublished) articleJSON.datePublished = datePublished;
      if (dateModified) articleJSON.dateModified = dateModified;
      if (articleSection) articleJSON.articleSection = articleSection;

      injectJSONLD(articleJSON);
    }

    if (breadcrumbItems && breadcrumbItems.length > 0) {
      const bcScript = document.createElement("script");
      bcScript.type = "application/ld+json";
      bcScript.setAttribute("data-seo-bc", "true");
      const existingBc = document.querySelector('script[data-seo-bc="true"]');
      if (existingBc) existingBc.remove();
      bcScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbItems.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url.startsWith("/") ? `${siteUrl}${item.url}` : item.url,
        })),
      }, null, 0);
      document.head.appendChild(bcScript);
      scripts.push(bcScript);
    }

    if (faqs && faqs.length > 0) {
      const faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.setAttribute("data-seo-faq", "true");
      const existingFaq = document.querySelector('script[data-seo-faq="true"]');
      if (existingFaq) existingFaq.remove();
      faqScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      }, null, 0);
      document.head.appendChild(faqScript);
      scripts.push(faqScript);
    }

    return () => {
      scripts.forEach((s) => s.remove());
    };
  }, [siteUrl, type, headline, description, datePublished, dateModified, articleSection, faqs, breadcrumbItems]);

  return null;
}